import { API_BASE_URL, REQUEST_TIMEOUT_MS } from "../../config/runtime";
import { authStore } from "../auth/authStore";
import { readCsrfToken } from "../auth/csrf";

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
  timeoutMs?: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function isMutation(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method);
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { requireAuth = true, timeoutMs = REQUEST_TIMEOUT_MS, ...fetchOptions } = options;
  const method = (fetchOptions.method ?? "GET").toUpperCase();
  const headers = new Headers(fetchOptions.headers);
  const token = authStore.getAccessToken();
  const csrfToken = readCsrfToken();

  headers.set("Accept", "application/json");
  if (fetchOptions.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (requireAuth && token) headers.set("Authorization", `Bearer ${token}`);
  if (isMutation(method) && csrfToken) headers.set("X-CSRF-Token", csrfToken);

  const controller = new AbortController();
  const timer = window.setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...fetchOptions,
      method,
      headers,
      credentials: "include",
      signal: controller.signal,
    });
    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      const detail =
        payload && typeof payload === "object" && "detail" in payload
          ? String(payload.detail)
          : `接口返回 ${String(response.status)}`;
      throw new ApiError(detail, response.status, payload);
    }

    return payload as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("请求超时，请稍后重试", 408);
    }
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
}

export const apiClient = { request };
